import { useMemo } from 'react';
import { getGaugeSizeVariant } from '@/components';
import { ContentDimensions } from '@/core';
import { calculateOptimalColumns } from './utils';

export const GAUGE_MIN_WIDTH = 100;
export const GAUGE_PANEL_PADDING_OFFSET = 16;

interface GaugeGridLayout {
  hasMultipleCharts: boolean;
  width: number;
  height: number;
  virtualizedChartSize: number;
}

export function useGaugeGridLayout(gaugeCount: number, contentDimensions: ContentDimensions): GaugeGridLayout {
  return useMemo(() => {
    const hasMultipleCharts = gaugeCount > 1;
    const layoutGaugeCount = Math.max(gaugeCount, 1);

    const columns = calculateOptimalColumns(
      layoutGaugeCount,
      contentDimensions.width,
      contentDimensions.height,
      GAUGE_PANEL_PADDING_OFFSET,
      GAUGE_MIN_WIDTH
    );
    const rows = Math.ceil(layoutGaugeCount / columns);

    const chartWidth = contentDimensions.width / columns - GAUGE_PANEL_PADDING_OFFSET;
    const chartHeight = contentDimensions.height / rows - GAUGE_PANEL_PADDING_OFFSET;
    const unclampedChartSize = Math.min(chartWidth, chartHeight);
    const chartSize = unclampedChartSize < GAUGE_MIN_WIDTH && hasMultipleCharts ? GAUGE_MIN_WIDTH : unclampedChartSize;

    const shouldUseContentDimensions = !hasMultipleCharts && getGaugeSizeVariant(chartSize) === 'extra-small';
    const width = shouldUseContentDimensions ? contentDimensions.width : chartSize;
    const height = shouldUseContentDimensions ? contentDimensions.height : chartSize;

    const virtualizedColumnCount = Math.max(
      1,
      Math.floor(contentDimensions.width / (GAUGE_MIN_WIDTH + GAUGE_PANEL_PADDING_OFFSET))
    );
    const virtualizedChartSize = Math.floor(
      contentDimensions.width / virtualizedColumnCount - GAUGE_PANEL_PADDING_OFFSET
    );

    return { hasMultipleCharts, width, height, virtualizedChartSize };
  }, [gaugeCount, contentDimensions.width, contentDimensions.height]);
}
